import { useState, useEffect } from 'react';
import useContactStore from '../../stores/useContactStore';
import contactService from '../../services/contactService';

/**
 * Contact Form Component
 * Create or edit a contact
 * Style: Odoo-like form
 */
export default function ContactForm({ contactId, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'individual',
    category: 'client',
    company_name: '',
    vat_number: '',
    street_address: '',
    city: '',
    postal_code: '',
    country: '',
    parent_contact_id: null,
    notes: '',
    is_active: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setError: setStoreError } = useContactStore();

  // Load contact if editing
  useEffect(() => {
    if (contactId) {
      loadContact();
    }
  }, [contactId]);

  const loadContact = async () => {
    setLoading(true);
    try {
      const response = await contactService.getContact(contactId);
      setFormData(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (loading) return;

    setError(null);
    setLoading(true);

    try {
      let response;
      if (contactId) {
        response = await contactService.updateContact(contactId, formData);
      } else {
        response = await contactService.createContact(formData);
      }

      if (onSave) {
        onSave(response.data);
      } else {
        setStoreError(null);
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && contactId) {
    return <div className="loading">Chargement du contact...</div>;
  }

  return (
    <div className="contact-form-container">
      <h3>{contactId ? 'Modifier le contact' : 'Créer un nouveau contact'}</h3>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="contact-form">
        {/* Basic Info Section */}
        <fieldset className="form-section">
          <legend>Informations de base</legend>

          <div className="form-group">
            <label htmlFor="name">Nom *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Jean Dupont"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
              >
                <option value="individual">Particulier</option>
                <option value="company">Société</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">Catégorie *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="client">Client</option>
                <option value="supplier">Fournisseur</option>
                <option value="partner">Partenaire</option>
                <option value="internal">Interne</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="jean@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Téléphone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+33 6 12 34 56 78"
              />
            </div>
          </div>
        </fieldset>

        {/* Company Info Section */}
        {formData.type === 'company' && (
          <fieldset className="form-section">
            <legend>Informations entreprise</legend>

            <div className="form-group">
              <label htmlFor="company_name">Nom de l'entreprise</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="Acme Corp"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vat_number">Numéro de TVA</label>
              <input
                type="text"
                id="vat_number"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleInputChange}
                placeholder="FR12345678901"
              />
            </div>
          </fieldset>
        )}

        {/* Address Section */}
        <fieldset className="form-section">
          <legend>Adresse</legend>

          <div className="form-group">
            <label htmlFor="street_address">Rue</label>
            <input
              type="text"
              id="street_address"
              name="street_address"
              value={formData.street_address}
              onChange={handleInputChange}
              placeholder="123 Rue de la Paix"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">Ville</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="Paris"
              />
            </div>

            <div className="form-group">
              <label htmlFor="postal_code">Code postal</label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                placeholder="75001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Pays</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="France"
              />
            </div>
          </div>
        </fieldset>

        {/* Additional Info */}
        <fieldset className="form-section">
          <legend>Informations complémentaires</legend>

          <div className="form-group">
            <label htmlFor="notes">Notes internes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Notes ou commentaires..."
              rows="4"
            />
          </div>

          <div className="form-group checkbox">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
            />
            <label htmlFor="is_active">Actif</label>
          </div>
        </fieldset>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Enregistrement...' : contactId ? 'Mettre à jour' : 'Créer le contact'}
          </button>
          {onClose && (
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Annuler
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
